import {
  useDispatchShippingCycleMutation,
  useGenerateShippingCycleMutation,
  useGetShippingTestJobQuery,
  useResetShippingCycleMutation,
  type ShippingTestAction,
  type ShippingTestJobStatus,
} from "@/services/shipping-test";
import { extractRtkErrorMessage } from "@/utils/extract-rtk-error";
import { useCallback, useState } from "react";

import { JOB_POLL_INTERVAL_MS, SETTLED_JOB_STATES } from "../constants";

export interface ShippingTestJobActions {
  run: (action: ShippingTestAction, cycle: string) => Promise<void>;
  clear: () => void;
  jobId: string | null;
  job: ShippingTestJobStatus | undefined;
  isSettled: boolean;
  isEnqueuing: boolean;
  error: string | null;
}

export const useShippingTestJob = (): ShippingTestJobActions => {
  const [jobId, setJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [generate, { isLoading: isGenerating }] =
    useGenerateShippingCycleMutation();
  const [dispatch, { isLoading: isDispatching }] =
    useDispatchShippingCycleMutation();
  const [reset, { isLoading: isResetting }] = useResetShippingCycleMutation();

  const [isSettled, setIsSettled] = useState<boolean>(false);

  const { data: job } = useGetShippingTestJobQuery(jobId ?? "", {
    skip: jobId === null,
    pollingInterval: isSettled ? 0 : JOB_POLL_INTERVAL_MS,
  });

  if (job !== undefined && SETTLED_JOB_STATES.includes(job.state) !== isSettled) {
    setIsSettled(!isSettled);
  }

  const run = useCallback(
    async (action: ShippingTestAction, cycle: string): Promise<void> => {
      setError(null);
      setJobId(null);
      setIsSettled(false);

      const trigger = { generate, dispatch, reset }[action];

      try {
        const enqueued: ShippingTestJobStatus = await trigger({
          cycle,
        }).unwrap();

        setJobId(enqueued.job_id);
      } catch (cause: unknown) {
        setError(extractRtkErrorMessage(cause, ""));
      }
    },
    [generate, dispatch, reset],
  );

  const clear = useCallback((): void => {
    setJobId(null);
    setError(null);
    setIsSettled(false);
  }, []);

  return {
    run,
    clear,
    jobId,
    job,
    isSettled,
    isEnqueuing: isGenerating || isDispatching || isResetting,
    error,
  };
};
